// Copyright ©️ 2026 eVolve MEP, LLC

import type { WorkOrderItem, UnitOfMeasureId, UpdateWorkOrderItemBody } from '@evolvemep/api-ts';
import { Flex, Text } from '@mantine/core';

import { NumberInputWrapped } from 'components/Mantine/NumberInputWrapped';
import { EditableCell } from 'helpers/ag-grid/EditableCells/EditableCell';
import { UnitOfMeasureSelect } from 'hooks/typeCacheHooks/UnitOfMeasureSelect';
import { useUnitOfMeasure } from 'hooks/typeCacheHooks/useUnitOfMeasure';

type Props = {
  item: WorkOrderItem;
  editable: boolean;
  updateItem: (item: Pick<UpdateWorkOrderItemBody, 'quantity' | 'unitOfMeasureId'>) => Promise<void>;
  /** @defaultValue true */
  thin?: boolean;
};

export const EditQtyMfg = ({ item, editable, updateItem, thin = true }: Props) => {
  const { uoms } = useUnitOfMeasure();

  return (
    <EditableCell
      miw={100}
      Editor={({ onChange, value, onSubmit }) => (
        <Flex gap="sm">
          <NumberInputWrapped
            w={90}
            max={999_999}
            clampBehavior="strict"
            min={1}
            label="Qty"
            required
            allowDecimal={false}
            value={value?.quantity}
            onChange={(v) => onChange({ ...value, quantity: v })}
            autoFocus
            data-autofocus
            onFocus={(e) => e.currentTarget.setSelectionRange(0, e.currentTarget.value.length)}
            onSubmit={onSubmit}
          />
          <UnitOfMeasureSelect
            required
            value={uoms?.find((m) => m.unitOfMeasureId === value?.unitOfMeasureId)}
            onChange={(v) => onChange({ ...value, unitOfMeasureId: v?.unitOfMeasureId ?? item.unitOfMeasureId })}
          />
        </Flex>
      )}
      className={`transparent-editable-cell ${thin ? 'detail-page-item-thin' : 'detail-page-right-align'}`}
      popoverPosition={thin ? undefined : 'bottom-end'}
      editable={editable}
      initialValue={
        {
          quantity: item.quantity,
          unitOfMeasureId: item.unitOfMeasureId,
        } as Partial<{
          quantity: number | null;
          unitOfMeasureId: UnitOfMeasureId;
        }>
      }
      onSave={async (v) => {
        if (v) {
          await updateItem({
            ...v,
            // Zero is not a valid value
            // oxlint-disable-next-line typescript/prefer-nullish-coalescing
            quantity: v.quantity || undefined,
          });
        }
      }}
      renderValue={
        <Text fz="sm" c="dimmed">
          {item.quantity} {uoms?.find((uom) => uom.unitOfMeasureId === item.unitOfMeasureId)?.code}
        </Text>
      }
    />
  );
};
